import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input, Checkbox, Switch, TagChipInput } from '../components';
import { CRM_PROVIDERS } from '../utils/crmProviderConfigs';
// import { numberFormatter } from '../styles/GlobalStyles';

const REHAB_LEVELS = [
    { key: 'turnkey', label: 'Turnkey' },
    { key: 'cosmetic', label: 'Cosmetic' },
    { key: 'light_rehab', label: 'Light Rehab' },
    { key: 'heavy_rehab', label: 'Heavy Rehab' },
    { key: 'gut_rehab', label: 'Gut Rehab' },
    { key: 'fire_damaged', label: 'Fire-Damaged' },
];

const PROPERTY_TYPES = [
    { key: 'single_family', label: 'Single Family' },
    { key: 'multi_family', label: 'Multi-Family' },
    { key: 'townhouse', label: 'Townhouse' },
    { key: 'condo', label: 'Condo' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'land', label: 'Land' },
    { key: 'portfolio', label: 'Portfolio' },
];

const STRATEGIES = [
    { key: 'buy_and_hold', label: 'Buy & Hold' },
    { key: 'fix_and_flip', label: 'Fix & Flip' },
    { key: 'brrrr', label: 'BRRRR' },
    { key: 'wholesale', label: 'Wholesale' },
    { key: 'turnkey', label: 'Turnkey' },
    { key: 'short_term_rental', label: 'Short-Term Rental' },
    { key: 'co_living', label: 'Co-Living' },
];

const ROLES = [
    { key: 'buyer', label: 'Buyer' },
    { key: 'agent', label: 'Agent' },
    { key: 'wholesaler', label: 'Wholesaler' },
    { key: 'bird_dog', label: 'Bird Dog' },
    { key: 'seller', label: 'Seller' },
    { key: 'commercial_buyer', label: 'Commercial Buyer' },
];

const OCCUPANCY_OPTIONS = [
    { key: 'vacant', label: 'Vacant' },
    { key: 'occupied', label: 'Occupied' },
    { key: 'section_8', label: 'Section 8' },
];

// Props:
//   crmProvider — 'ghl' | 'fub' | 'salesforce' | null. When set (a CRM is
//                 connected) and this is a fresh buyer (not the buy box, not an
//                 edit), the form shows a CRM tags field. The tags are saved on
//                 the buyer as data.crmTags and applied to the CRM contact by the
//                 onCreate sync (syncBuyerToCRM → syncSingleBuyerToCRM), whether
//                 that contact is new or already existed in the CRM.
//   crmLoading  — true while the page is still working out which CRM is
//                 connected. The field is shown (generic "CRM" label) during
//                 that window rather than hidden, so an operator who opens the
//                 form the instant the page loads is never silently denied it.
//                 Once they have typed a tag the field STAYS, even if the probe
//                 then says no CRM is connected — an input is never pulled out
//                 from under the operator, and the tags they entered are saved
//                 on the buyer (they apply on a later sync if a CRM is
//                 connected afterwards).
//
// Tags are capped the same way the MCP path caps them (per-tag length, count)
// so data.crmTags means the same thing whichever door wrote it.
const CRM_TAG_MAX_LEN = 100;
const CRM_TAG_MAX_COUNT = 20;
const capCrmTags = (tags) => tags.map((t) => Array.from(t).slice(0, CRM_TAG_MAX_LEN).join('')).slice(0, CRM_TAG_MAX_COUNT);

const CreateBuyerModal = forwardRef(function CreateBuyerModal({ onSubmit, loading, error, onValidityChange, initialData, hideContact, crmProvider = null, crmLoading = false }, ref) {
    const toStr = (val) => (val != null && val !== '' ? String(val) : '');

    // Contact
    const [name, setName] = useState(toStr(initialData?.contact?.name));
    const [email, setEmail] = useState(toStr(initialData?.contact?.email));
    const [phone, setPhone] = useState(toStr(initialData?.contact?.phone));

    // Source & Background
    const [source, setSource] = useState(toStr(initialData?.source));
    const [background, setBackground] = useState(toStr(initialData?.background));

    // CRM tags (create-only; see showCrmTags)
    const [crmTags, setCrmTags] = useState([]);
    const showCrmTags = !hideContact && !initialData && (!!crmProvider || crmLoading || crmTags.length > 0);
    const crmLabel = CRM_PROVIDERS[crmProvider]?.crmLabel || 'CRM';

    // Geography (simplified)
    const [zipCodes, setZipCodes] = useState(
        initialData?.geography?.zipCodes?.length ? initialData.geography.zipCodes.join(', ') : ''
    );

    // Price range
    const [priceMin, setPriceMin] = useState(toStr(initialData?.priceRange?.min));
    const [priceMax, setPriceMax] = useState(toStr(initialData?.priceRange?.max));

    // Profit thresholds
    const [minProfitDollars, setMinProfitDollars] = useState(toStr(initialData?.profitThresholds?.minProfitDollars));
    const [minProfitPct, setMinProfitPct] = useState(toStr(initialData?.profitThresholds?.minProfitPercentage));
    const [maxArvPct, setMaxArvPct] = useState(toStr(initialData?.profitThresholds?.maxPurchasePricePercentOfARV));
    const [minRoiPct, setMinRoiPct] = useState(toStr(initialData?.profitThresholds?.minRoiPercent));
    // Stored-only rental return metrics — recorded for reference, not used by
    // deal matching.
    const [minCashOnCash, setMinCashOnCash] = useState(toStr(initialData?.profitThresholds?.minCashOnCashReturn));
    const [minNetCashFlow, setMinNetCashFlow] = useState(toStr(initialData?.profitThresholds?.minNetCashFlow));
    // Max total cash the buyer will put into one deal, all-in and
    // financing-agnostic (see helptext). Stored only — not matched.
    const [maxCashInvested, setMaxCashInvested] = useState(toStr(initialData?.profitThresholds?.maxCashInvested));

    // Rehab levels
    const [selectedRehabLevels, setSelectedRehabLevels] = useState(initialData?.rehabLevels || []);

    // Property types
    const [selectedPropertyTypes, setSelectedPropertyTypes] = useState(initialData?.propertyTypes || []);

    // Strategies
    const [selectedStrategies, setSelectedStrategies] = useState(initialData?.strategies || []);

    // Property requirements
    const [minBeds, setMinBeds] = useState(toStr(initialData?.propertyRequirements?.minBedrooms));
    const [maxBeds, setMaxBeds] = useState(toStr(initialData?.propertyRequirements?.maxBedrooms));
    const [minBaths, setMinBaths] = useState(toStr(initialData?.propertyRequirements?.minBathrooms));
    const [maxBaths, setMaxBaths] = useState(toStr(initialData?.propertyRequirements?.maxBathrooms));
    const [minSqft, setMinSqft] = useState(toStr(initialData?.propertyRequirements?.minSquareFeet));
    const [maxSqft, setMaxSqft] = useState(toStr(initialData?.propertyRequirements?.maxSquareFeet));
    const [minYearBuilt, setMinYearBuilt] = useState(toStr(initialData?.propertyRequirements?.minYearBuilt));
    const [maxYearBuilt, setMaxYearBuilt] = useState(toStr(initialData?.propertyRequirements?.maxYearBuilt));

    // Deal structure
    const [acceptsSellerFinancing, setAcceptsSellerFinancing] = useState(initialData?.dealStructure?.acceptsSellerFinancing || false);
    const [acceptsSubTo, setAcceptsSubTo] = useState(initialData?.dealStructure?.acceptsSubTo || false);
    const [maxInterestRate, setMaxInterestRate] = useState(
        typeof initialData?.dealStructure?.maxInterestRate === 'number' ? String(initialData.dealStructure.maxInterestRate) : ''
    );
    const [maxDownPayment, setMaxDownPayment] = useState(
        typeof initialData?.dealStructure?.maxDownPayment === 'number' ? String(initialData.dealStructure.maxDownPayment) : ''
    );
    // Minimum acceptable years before a balloon payment comes due. Stored only —
    // not used by deal matching. Only meaningful for creative-financing deals.
    const [minBalloonYears, setMinBalloonYears] = useState(
        typeof initialData?.dealStructure?.minBalloonYears === 'number' ? String(initialData.dealStructure.minBalloonYears) : ''
    );

    // Amenities
    const [requiresPool, setRequiresPool] = useState(initialData?.propertyRequirements?.requiresPool || false);
    const [requiresGarage, setRequiresGarage] = useState(initialData?.propertyRequirements?.requiresGarage || false);
    const [minGarageSpaces, setMinGarageSpaces] = useState(toStr(initialData?.propertyRequirements?.minGarageSpaces));

    // Roles
    const [selectedRoles, setSelectedRoles] = useState(initialData?.roles || []);

    // Property preferences
    const [floodZoneBuyer, setFloodZoneBuyer] = useState(initialData?.propertyRequirements?.floodZoneBuyer || false);
    const [selectedOccupancy, setSelectedOccupancy] = useState(initialData?.propertyRequirements?.occupancyPreference || []);
    const [slabBuyer, setSlabBuyer] = useState(initialData?.propertyRequirements?.slabBuyer || false);
    const [warZoneBuyer, setWarZoneBuyer] = useState(initialData?.propertyRequirements?.warZoneBuyer || false);

    const toggleRole = useCallback((key) => {
        setSelectedRoles(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    const toggleRehabLevel = useCallback((key) => {
        setSelectedRehabLevels(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    // Parse a financing-cap input (rate or dollar) to a number, or null when
    // blank/invalid. Strips currency/percent formatting.
    const parseFinancingCap = (raw) => {
        if (raw == null) return null;
        const cleaned = String(raw).replace(/[^0-9.]/g, '');
        if (cleaned === '') return null;
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : null;
    };

    const togglePropertyType = useCallback((key) => {
        setSelectedPropertyTypes(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    const toggleStrategy = useCallback((key) => {
        setSelectedStrategies(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    const toggleOccupancy = useCallback((key) => {
        setSelectedOccupancy(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    const parseNum = (val) => {
        if (!val || val.trim() === '') return null;
        const cleaned = val.replace(/[$,%,]/g, '');
        const num = parseFloat(cleaned);
        return isNaN(num) ? null : num;
    };

    const handleSubmit = useCallback(() => {
        if (!hideContact && !name.trim()) return;

        // Parse ZIP codes
        const parsedZips = zipCodes
            .split(/[,\s]+/)
            .map(z => z.trim())
            .filter(z => /^\d{5}$/.test(z));

        const buyerData = {
            contact: hideContact
                ? (initialData?.contact || { name: "My Buy Box", email: null, phone: null })
                : { name: name.trim(), email: email.trim() || null, phone: phone.trim() || null },
            roles: selectedRoles.length > 0 ? selectedRoles : null,
            geography: {
                states: initialData?.geography?.states || [],
                counties: initialData?.geography?.counties || [],
                zipCodes: parsedZips,
                polygons: initialData?.geography?.polygons || [],
            },
            priceRange: {
                min: parseNum(priceMin),
                max: parseNum(priceMax),
            },
            profitThresholds: {
                minProfitDollars: parseNum(minProfitDollars),
                minProfitPercentage: parseNum(minProfitPct),
                maxPurchasePricePercentOfARV: parseNum(maxArvPct),
                minRoiPercent: parseNum(minRoiPct),
                // Stored-only rental return metrics (not matched against deals).
                minCashOnCashReturn: parseNum(minCashOnCash),
                minNetCashFlow: parseNum(minNetCashFlow),
                // Stored-only capital ceiling (all-in cash per deal).
                maxCashInvested: parseNum(maxCashInvested),
            },
            rehabLevels: selectedRehabLevels.length > 0 ? selectedRehabLevels : null,
            propertyTypes: selectedPropertyTypes.length > 0 ? selectedPropertyTypes : null,
            strategies: selectedStrategies.length > 0 ? selectedStrategies : null,
            propertyRequirements: {
                minBedrooms: parseNum(minBeds),
                maxBedrooms: parseNum(maxBeds),
                minBathrooms: parseNum(minBaths),
                maxBathrooms: parseNum(maxBaths),
                minSquareFeet: parseNum(minSqft),
                maxSquareFeet: parseNum(maxSqft),
                minYearBuilt: parseNum(minYearBuilt),
                maxYearBuilt: parseNum(maxYearBuilt),
                requiresPool,
                requiresGarage,
                minGarageSpaces: parseNum(minGarageSpaces),
                floodZoneBuyer,
                occupancyPreference: selectedOccupancy.length > 0 ? selectedOccupancy : [],
                slabBuyer,
                warZoneBuyer,
            },
            dealStructure: {
                acceptsSellerFinancing,
                acceptsSubTo,
                // Caps only apply to creative-financing deals; drop stale values
                // when neither option is enabled.
                maxInterestRate: (acceptsSellerFinancing || acceptsSubTo) ? parseFinancingCap(maxInterestRate) : null,
                maxDownPayment: (acceptsSellerFinancing || acceptsSubTo) ? parseFinancingCap(maxDownPayment) : null,
                // Stored-only; balloon terms only apply to creative-financing deals.
                minBalloonYears: (acceptsSellerFinancing || acceptsSubTo) ? parseFinancingCap(minBalloonYears) : null,
            },
            source: hideContact ? (initialData?.source || null) : (source.trim() || null),
            background: hideContact ? (initialData?.background || null) : (background.trim() || null),
            active: true,
            wantsDeals: true,
        };

        // Only stamp crmTags when the field was offered — keeps edit payloads
        // (which updateBuyer flattens into a partial patch) from touching it.
        if (showCrmTags) {
            buyerData.crmTags = crmTags.length > 0 ? capCrmTags(crmTags) : null;
        }

        onSubmit(buyerData);
    }, [name, email, phone, source, background, crmTags, showCrmTags, zipCodes, priceMin, priceMax,
        minProfitDollars, minProfitPct, maxArvPct, minRoiPct,
        minCashOnCash, minNetCashFlow, maxCashInvested,
        selectedRehabLevels, selectedPropertyTypes, selectedRoles, selectedStrategies,
        minBeds, maxBeds, minBaths, maxBaths, minSqft, maxSqft,
        minYearBuilt, maxYearBuilt, acceptsSellerFinancing, acceptsSubTo,
        maxInterestRate, maxDownPayment, minBalloonYears,
        requiresPool, requiresGarage, minGarageSpaces,
        floodZoneBuyer, selectedOccupancy, slabBuyer, warZoneBuyer,
        onSubmit, hideContact, initialData]);

    const isValid = hideContact ? true : name.trim().length > 0;

    useImperativeHandle(ref, () => ({
        submit: handleSubmit
    }), [handleSubmit]);

    useEffect(() => {
        if (onValidityChange) onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    return (
        <div className="create-buyer-modal-container">
            {/* Contact Info */}
            {!hideContact && <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Contact Information</h3>
                <Input
                    label="Name"
                    labelType="regular"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Buyer name"
                    type="text"
                    leftUnit={null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={loading}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                <div className="create-buyer-row">
                    <Input
                        label="Email"
                        labelType="regular"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email address"
                        type="email"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Phone"
                        labelType="regular"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone number"
                        type="tel"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>}

            {/* Roles */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Role(s)</h3>
                <div className="create-buyer-checkboxes">
                    {ROLES.map(role => (
                        <div
                            key={role.key}
                            className="create-buyer-checkbox-item"
                            onClick={() => !loading && toggleRole(role.key)}
                        >
                            <Checkbox
                                label={role.label}
                                selected={selectedRoles.includes(role.key)}
                                disabled={loading}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Source & Background */}
            {!hideContact && <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Source & Background</h3>
                <Input
                    label="Source"
                    labelType="regular"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="e.g. REI meetup, BiggerPockets"
                    type="text"
                    leftUnit={null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={loading}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                <Input
                    label="Background"
                    labelType="regular"
                    value={background}
                    onChange={(e) => setBackground(e.target.value)}
                    placeholder="How did you meet this buyer? What's the relationship?"
                    type="text"
                    leftUnit={null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={loading}
                    multiline={true}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={true}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                {showCrmTags && (
                    <TagChipInput
                        id="create-buyer-crm-tags"
                        label={`${crmLabel} tags (optional)`}
                        value={crmTags}
                        onChange={setCrmTags}
                        placeholder="Add tags (Enter to commit)"
                        disabled={loading}
                    />
                )}
            </div>}

            {/* Geography */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Geography</h3>
                <Input
                    label="ZIP Codes"
                    labelType="regular"
                    value={zipCodes}
                    onChange={(e) => setZipCodes(e.target.value)}
                    placeholder="Enter ZIP codes separated by commas (e.g. 63101, 63102, 63103)"
                    type="text"
                    leftUnit={null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={loading}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
                <span className="body-regular colour-quaternary">
                    You can add counties and map zones after creating the buyer.
                </span>
            </div>

            {/* Price Range */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Price Range</h3>
                <div className="create-buyer-row">
                    <Input
                        label="Min Price"
                        labelType="regular"
                        value={priceMin}
                        onChange={(e) => setPriceMin(e.target.value)}
                        placeholder="0"
                        type="text"
                        leftUnit="$"
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Max Price"
                        labelType="regular"
                        value={priceMax}
                        onChange={(e) => setPriceMax(e.target.value)}
                        placeholder="No max"
                        type="text"
                        leftUnit="$"
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            {/* Profit Thresholds */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Profit Thresholds</h3>
                <div className="create-buyer-row">
                    <Input
                        label="Min Profit ($)"
                        labelType="regular"
                        value={minProfitDollars}
                        onChange={(e) => setMinProfitDollars(e.target.value)}
                        placeholder="0"
                        type="text"
                        leftUnit="$"
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Min Profit (%)"
                        labelType="regular"
                        value={minProfitPct}
                        onChange={(e) => setMinProfitPct(e.target.value)}
                        placeholder="0"
                        type="text"
                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <div className="create-buyer-row">
                    <Input
                        label="Max % of ARV"
                        labelType="regular"
                        value={maxArvPct}
                        onChange={(e) => setMaxArvPct(e.target.value)}
                        placeholder="e.g. 70"
                        type="text"
                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Min ROI (%)"
                        labelType="regular"
                        value={minRoiPct}
                        onChange={(e) => setMinRoiPct(e.target.value)}
                        placeholder="0"
                        type="text"
                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <div className="create-buyer-row">
                    <Input
                        label="Min Cash-on-Cash"
                        labelType="regular"
                        value={minCashOnCash}
                        onChange={(e) => setMinCashOnCash(e.target.value)}
                        placeholder="0"
                        type="text"
                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Min Net Cash Flow"
                        labelType="regular"
                        value={minNetCashFlow}
                        onChange={(e) => setMinNetCashFlow(e.target.value)}
                        placeholder="Per month"
                        type="text"
                        leftUnit="$"
                        rightUnit="/mo"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <div className="create-buyer-row">
                    <Input
                        label="Max Cash Invested"
                        labelType="regular"
                        value={maxCashInvested}
                        onChange={(e) => setMaxCashInvested(e.target.value)}
                        placeholder="No cap"
                        type="text"
                        leftUnit="$"
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <span className="body-regular colour-tertiary">
                    The most of their own cash the buyer will put into one deal — down payment, closing, points, and any repairs they don't finance. Borrowed money (mortgage, hard money, seller financing) doesn't count.
                </span>
            </div>

            {/* Rehab Levels */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Rehab Levels</h3>
                <div className="create-buyer-checkboxes">
                    {REHAB_LEVELS.map(level => (
                        <div
                            key={level.key}
                            className="create-buyer-checkbox-item"
                            onClick={() => !loading && toggleRehabLevel(level.key)}
                        >
                            <Checkbox
                                label={level.label}
                                selected={selectedRehabLevels.includes(level.key)}
                                disabled={loading}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Property Types */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Property Types</h3>
                <div className="create-buyer-checkboxes">
                    {PROPERTY_TYPES.map(type => (
                        <div
                            key={type.key}
                            className="create-buyer-checkbox-item"
                            onClick={() => !loading && togglePropertyType(type.key)}
                        >
                            <Checkbox
                                label={type.label}
                                selected={selectedPropertyTypes.includes(type.key)}
                                disabled={loading}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Strategies */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Strategies</h3>
                <div className="create-buyer-checkboxes">
                    {STRATEGIES.map(strategy => (
                        <div
                            key={strategy.key}
                            className="create-buyer-checkbox-item"
                            onClick={() => !loading && toggleStrategy(strategy.key)}
                        >
                            <Checkbox
                                label={strategy.label}
                                selected={selectedStrategies.includes(strategy.key)}
                                disabled={loading}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Property Requirements */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Property Requirements</h3>
                <div className="create-buyer-row">
                    <Input
                        label="Min Beds"
                        labelType="regular"
                        value={minBeds}
                        onChange={(e) => setMinBeds(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Max Beds"
                        labelType="regular"
                        value={maxBeds}
                        onChange={(e) => setMaxBeds(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Min Baths"
                        labelType="regular"
                        value={minBaths}
                        onChange={(e) => setMinBaths(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Max Baths"
                        labelType="regular"
                        value={maxBaths}
                        onChange={(e) => setMaxBaths(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <div className="create-buyer-row">
                    <Input
                        label="Min Sq Ft"
                        labelType="regular"
                        value={minSqft}
                        onChange={(e) => setMinSqft(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Max Sq Ft"
                        labelType="regular"
                        value={maxSqft}
                        onChange={(e) => setMaxSqft(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Min Year Built"
                        labelType="regular"
                        value={minYearBuilt}
                        onChange={(e) => setMinYearBuilt(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                    <Input
                        label="Max Year Built"
                        labelType="regular"
                        value={maxYearBuilt}
                        onChange={(e) => setMaxYearBuilt(e.target.value)}
                        placeholder="Any"
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={loading}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            </div>

            {/* Amenities */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Amenities</h3>
                <div className="create-buyer-toggles">
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Requires Pool</span>
                        <Switch
                            val={requiresPool}
                            setVal={setRequiresPool}
                            disabled={loading}
                        />
                    </div>
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Requires Garage</span>
                        <Switch
                            val={requiresGarage}
                            setVal={setRequiresGarage}
                            disabled={loading}
                        />
                    </div>
                    {requiresGarage && (
                        <div className="create-buyer-row">
                            <Input
                                label="Min Garage Spaces"
                                labelType="regular"
                                value={minGarageSpaces}
                                onChange={(e) => setMinGarageSpaces(e.target.value)}
                                placeholder="Any"
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={loading}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                                submitPromptDisabled={false}
                                submitPrompt={null}
                                onHeightChange={null}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Occupancy Preference */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Occupancy Preference</h3>
                <div className="create-buyer-checkboxes">
                    {OCCUPANCY_OPTIONS.map(opt => (
                        <div
                            key={opt.key}
                            className="create-buyer-checkbox-item"
                            onClick={() => !loading && toggleOccupancy(opt.key)}
                        >
                            <Checkbox
                                label={opt.label}
                                selected={selectedOccupancy.includes(opt.key)}
                                disabled={loading}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Property Preferences */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Property Preferences</h3>
                <div className="create-buyer-toggles">
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Will buy in a flood zone</span>
                        <Switch
                            val={floodZoneBuyer}
                            setVal={setFloodZoneBuyer}
                            disabled={loading}
                        />
                    </div>
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Slab buyer</span>
                        <Switch
                            val={slabBuyer}
                            setVal={setSlabBuyer}
                            disabled={loading}
                        />
                    </div>
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Will buy in a war zone</span>
                        <Switch
                            val={warZoneBuyer}
                            setVal={setWarZoneBuyer}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>

            {/* Deal Structure */}
            <div className="create-buyer-section">
                <h3 className="body-semibold colour-primary create-buyer-section-title">Deal Structure</h3>
                <div className="create-buyer-toggles">
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Accepts Seller Financing</span>
                        <Switch
                            val={acceptsSellerFinancing}
                            setVal={setAcceptsSellerFinancing}
                            disabled={loading}
                        />
                    </div>
                    <div className="create-buyer-toggle-row">
                        <span className="body-regular colour-primary">Accepts Sub-To</span>
                        <Switch
                            val={acceptsSubTo}
                            setVal={setAcceptsSubTo}
                            disabled={loading}
                        />
                    </div>
                </div>
                {(acceptsSellerFinancing || acceptsSubTo) && (
                    <div className="create-buyer-row">
                        <Input
                            label="Max Interest Rate"
                            labelType="regular"
                            value={maxInterestRate}
                            onChange={(e) => setMaxInterestRate(e.target.value)}
                            placeholder="No cap"
                            type="text"
                            leftUnit={null}
                            rightUnit="%"
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={loading}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                        <Input
                            label="Max Down Payment"
                            labelType="regular"
                            value={maxDownPayment}
                            onChange={(e) => setMaxDownPayment(e.target.value)}
                            placeholder="No cap"
                            type="text"
                            leftUnit="$"
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={loading}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                        <Input
                            label="Min Balloon"
                            labelType="regular"
                            value={minBalloonYears}
                            onChange={(e) => setMinBalloonYears(e.target.value)}
                            placeholder="No min"
                            type="text"
                            leftUnit={null}
                            rightUnit="yrs"
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={loading}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                    </div>
                )}
            </div>

            {error && (
                <div className="create-buyer-error body-regular">
                    {error}
                </div>
            )}
        </div>
    );
});

export default CreateBuyerModal;
